import { Int, MsgExecuteContract } from "@terra-money/terra.js"
import { useAddress } from "hooks"
import { div, floor, times } from "libs/math"
import { toAmount } from "libs/parse"
import { Type } from "pages/Swap"
import { useEffect, useMemo, useState } from "react"
import { tokenInfos } from "./usePairs"
import {
  Coin,
  Coins,
  CreateTxOptions,
  Fee,
  Numeric,
} from "@terra-money/terra.js"

// Create an array of points on the graph
const SIGMOID_CURVE = [
  { supply: 0, spot_price: 0.000001, reserve: 0 },
  { supply: 1000000, spot_price: 0.878077496907624, reserve: 439039.248453812 },
  { supply: 2000000, spot_price: 0.451122232313246, reserve: 1103639.11306425 },
  { supply: 3000000, spot_price: 0.209063046712477, reserve: 1433731.75257711 },
  { supply: 4000000, spot_price: 0.151899940105316, reserve: 1614213.24598601 },
  { supply: 5000000, spot_price: 0.279632912491762, reserve: 1829979.67228454 },
  { supply: 6000000, spot_price: 0.592261963871819, reserve: 2265927.11046633 },
  { supply: 7000000, spot_price: 1.08978709424548, reserve: 3106951.63952498 },
  { supply: 8000000, spot_price: 1.77220830361275, reserve: 4537949.3384541 },
  { supply: 9000000, spot_price: 2.63952559197363, reserve: 6743816.28624729 },
  { supply: 10000000, spot_price: 3.69173895932812, reserve: 9909448.56189817 },
  { supply: 11000000, spot_price: 4.92884840567622, reserve: 14219742.2444003 },
  { supply: 12000000, spot_price: 6.35085393101792, reserve: 19859593.4127474 },
  { supply: 13000000, spot_price: 7.95775553535323, reserve: 27013898.145933 },
  { supply: 14000000, spot_price: 9.74955321868215, reserve: 35867552.5229507 },
  { supply: 15000000, spot_price: 11.7262469810047, reserve: 46605452.6227941 },
  { supply: 20000000, spot_price: 24.3831569775215, reserve: 136878962.519109 },
  { supply: 25000000, spot_price: 41.6624689488784, reserve: 301993027.335109 },
  { supply: 40000000, spot_price: 121.234816711991, reserve: 1523722669.79163 },
  { supply: 60000000, spot_price: 292.044908043903, reserve: 5656519917.35056 },
  { supply: 75000000, spot_price: 468.687697278659, reserve: 11362014457.2698 },
  {
    supply: 100000000,
    spot_price: 855.540385500058,
    reserve: 27914865492.0037,
  },
  {
    supply: 107000000,
    spot_price: 984.567499049333,
    reserve: 34355243087.9266,
  },
  {
    supply: 108000000,
    spot_price: 1003.73952815806,
    reserve: 35349396601.5303,
  },
  {
    supply: 109000000,
    spot_price: 1023.09645334578,
    reserve: 36362814592.2822,
  },
  { supply: 110000000, spot_price: 1042.6382746125, reserve: 37395681956.2614 },
]

type Params = {
  from: string
  to: string
  amount: number | string
  type?: Type
  staking_days: number
  description: string
}

const useTBC = (params: Params) => {
  const walletAddress = useAddress()
  const { from, to, type, amount: _amount, staking_days, description } = params

  const amount = Number(_amount)
  const debouncedAmount = useDebounce(amount, 500)
  const [isLoading, setIsLoading] = useState(false)
  const [msgs, setMsgs] = useState<
    (MsgExecuteContract[] | MsgExecuteContract)[]
  >([])
  const [autoRefreshTicker, setAutoRefreshTicker] = useState(false)
  const [simulatedAmounts, setSimulatedAmounts] = useState<number[]>([])

  // From api to smart contract
  let [currentSupply, setCurrentSupply] = useState(0)
  let [currentPrice, setCurrentPrice] = useState(0)
  let [currentReserve, setCurrentReserve] = useState(0)
  let [taxCollected, setTaxCollected] = useState(0)
  let [luna1Price, setLuna1Price] = useState(0)

  let currentMarketCap = 0
  const tokenInfo = tokenInfos.get(to)

  // Will calculate new values
  let tokensToMint = 0
  let reserveReturned = 0
  let newMarketCap = 0

  const profitableQuery = useMemo(() => {
    let luncUsdPrice = 0
    let luncLbunPrice = 0
    let coinDeposit = 0
    let coinDebit = 0
    let msg = undefined

    let newReserve
    let newSupply
    let newPrice
    let simulatedAmount = 0
    let rate = 0

    setIsLoading(true)
    if (!to || !debouncedAmount) {
      return undefined
    } else if (from === "uluna") {
      const taxPercentage = 0.02
      let index = 0
      coinDeposit = debouncedAmount * (1 - (taxPercentage + 0.01074))
      newReserve = currentReserve + coinDeposit

      if (
        newReserve == 0 ||
        newReserve > SIGMOID_CURVE[SIGMOID_CURVE.length - 1].reserve
      ) {
        return undefined
      }

      for (let i = 0; i < SIGMOID_CURVE.length; i++) {
        if (
          newReserve > SIGMOID_CURVE[i].reserve &&
          newReserve <= SIGMOID_CURVE[i + 1].reserve
        ) {
          index = i
          break
        }
      }

      //Shift to zero
      let virtualReserve = newReserve - SIGMOID_CURVE[index].reserve

      //Calc slope of line
      let slope =
        (SIGMOID_CURVE[index + 1].spot_price -
          SIGMOID_CURVE[index].spot_price) /
        (SIGMOID_CURVE[index + 1].supply - SIGMOID_CURVE[index].supply)

      /*https://www.wolframalpha.com/input?i=R+%3D+L*S+%2B+.5*%28U-L%29S+%3B++++solve+for+R*/
      //calc lower and upper y values
      let lower = SIGMOID_CURVE[index].spot_price
      let upper = Math.sqrt(lower ** 2 + 2 * slope * virtualReserve)

      //calc supply from reserve
      let virtualSupply = (2 * virtualReserve) / (lower + upper)

      //Shift back
      let realSupply = virtualSupply + SIGMOID_CURVE[index].supply

      //tokens to mint depends on whether currentSupply is within current SIGMOID_CURVE bracket
      let tokensToMint = realSupply - currentSupply

      if (tokensToMint < 0) {
        tokensToMint = 0
      }

      simulatedAmount = Math.floor(tokensToMint * 1000000) //cuz 6 decimals
      simulatedAmount = simulatedAmount < 0 ? 0 : simulatedAmount

      rate = debouncedAmount / tokensToMint
      rate = rate < 0 ? 0 : rate

      let spot_price = slope * virtualSupply + SIGMOID_CURVE[index].spot_price

      const buyAmount = Math.floor(debouncedAmount * 1000000) //cuz 6 decimals

      const executeMsg = {
        buy: {},
      }

      msg = new MsgExecuteContract(
        walletAddress,
        "terra1wez9puj43v4s25vrex7cv3ut3w75w4h6j5e537sujyuxj0r5ne2qp9uwl9",
        executeMsg,
        { uluna: buyAmount.toString() }
      )

      //selling
    } else if (
      from ===
      "terra1wez9puj43v4s25vrex7cv3ut3w75w4h6j5e537sujyuxj0r5ne2qp9uwl9"
    ) {
      const taxPercentage = 0.015
      let index = 0
      coinDebit = debouncedAmount
      newSupply = currentSupply - coinDebit //this is FROG

      //newSupply calculation
      if (
        newSupply < 0 ||
        newSupply > SIGMOID_CURVE[SIGMOID_CURVE.length - 1].supply
      ) {
        return undefined
      }

      for (let i = 0; i < SIGMOID_CURVE.length; i++) {
        if (
          newSupply > SIGMOID_CURVE[i].supply &&
          newSupply <= SIGMOID_CURVE[i + 1].supply
        ) {
          index = i
          break
        }
      }

      //Shift to zero
      let virtual_supply = newSupply - SIGMOID_CURVE[index].supply

      //Calc slope of line
      let slope =
        (SIGMOID_CURVE[index + 1].spot_price -
          SIGMOID_CURVE[index].spot_price) /
        (SIGMOID_CURVE[index + 1].supply - SIGMOID_CURVE[index].supply)

      /*https://www.wolframalpha.com/input?i=R+%3D+L*S+%2B+.5*%28U-L%29S+%3B++++solve+for+S*/
      //calc lower and upper y values
      let lower = SIGMOID_CURVE[index].spot_price
      let upper = slope * virtual_supply + lower

      //calc reserve from supply
      let virtual_reserve = (virtual_supply * (lower + upper)) / 2

      //Shift back
      let newReserve = virtual_reserve + SIGMOID_CURVE[index].reserve

      //Subtract to get reserve difference
      let reserveDelta = currentReserve - newReserve

      //subtract tax
      let reserveReturned = reserveDelta * (1 - taxPercentage)

      //Convert to Uint128
      simulatedAmount = reserveReturned * 1000000 //cuz 6 decimals
      simulatedAmount = simulatedAmount < 0 ? 0 : simulatedAmount
      simulatedAmount = Math.floor(simulatedAmount)

      //cal Ave rate for this order
      rate = reserveReturned / coinDebit
      rate = rate < 0 ? 0 : rate

      const burnAmount = Math.floor(parseFloat(`${coinDebit}`) * 1000000)
      msg = new MsgExecuteContract(
        walletAddress, //sender
        "terra1wez9puj43v4s25vrex7cv3ut3w75w4h6j5e537sujyuxj0r5ne2qp9uwl9", //TBC contract
        { burn: { amount: burnAmount.toString() } }
      )
    } else {
      return undefined
    }

    luncUsdPrice = luna1Price
    luncLbunPrice = rate

    const tokenRoutes: string[] = []
    setIsLoading(false)
    return {
      msg,
      luncUsdPrice,
      luncLbunPrice: luncLbunPrice.toString(),
      simulatedAmount,
      tokenRoutes,
      price: rate.toString(),
      //currentSupply,
      //currentReserve,
      //currentPrice
    }
  }, [to, debouncedAmount, msgs, simulatedAmounts, staking_days, description]) //end of useMemo

  useEffect(() => {
    const url1 =
      "https://lcd.miata-ipfs.com/cosmwasm/wasm/v1/contract/terra1wez9puj43v4s25vrex7cv3ut3w75w4h6j5e537sujyuxj0r5ne2qp9uwl9/smart/eyJjdXJ2ZV9pbmZvIjp7fX0="
    fetch(url1)
      .then((response) => response.text())
      .then((text) => {
        console.log(text)
        const tbcData = JSON.parse(text)
        setCurrentSupply(Number(tbcData.data.supply) / 1000000)
        setCurrentPrice(Number(tbcData.data.spot_price) / 1000000)
        setCurrentReserve(Number(tbcData.data.reserve) / 1000000)
        setTaxCollected(Number(tbcData.data.tax_collected) / 1000000)
      })

    const url2 =
      "https://api.coingecko.com/api/v3/simple/price?ids=terra-luna&vs_currencies=usd"
    fetch(url2)
      .then((response) => response.text())
      .then((text) => {
        console.log(text)
        const apiCoinGeckoLuna = JSON.parse(text)
        setLuna1Price(Number(apiCoinGeckoLuna["terra-luna"]["usd"]))
      })
    return
  }, [debouncedAmount, from, to, type, isLoading])

  useEffect(() => {
    const timerId = setInterval(() => {
      setAutoRefreshTicker((current) => !current)
    }, 30000)
    return () => {
      clearInterval(timerId)
    }
  }, [debouncedAmount, from])

  const result = useMemo(() => {
    if (!from || !to || !type || !debouncedAmount) {
      return { profitableQuery: undefined, isLoading: false }
    }
    return {
      isLoading,
      profitableQuery,
    }
  }, [debouncedAmount, from, isLoading, profitableQuery, to, type])

  return result
}

// Generic deBounce Hook ( https://usehooks.com/useDebounce/ )
function useDebounce(value: number, delay: number) {
  // State and setters for debounced value
  const [debouncedValue, setDebouncedValue] = useState(value)
  useEffect(
    () => {
      // Update debounced value after delay
      const handler = setTimeout(() => {
        setDebouncedValue(value)
      }, delay)
      // Cancel the timeout if value changes (also on delay change or unmount)
      // This is how we prevent debounced value from updating if value is changed ...
      // .. within the delay period. Timeout gets cleared and restarted.
      return () => {
        clearTimeout(handler)
      }
    },
    [value, delay] // Only re-call effect if value or delay changes
  )
  return debouncedValue
}

export default useTBC
